import { useEffect, useMemo, useRef, useState } from 'react';
import './styles/journal.css';
import battleBackground from './assets/battle/bg.jpg';
import battlePlatform from './assets/battle/platform.png';
import hassanTrainer from './assets/battle/EnemyTrainer.png';
import playerTrainer from './assets/battle/PlayerTrainer.png';
import resumePdf from './assets/resume.pdf';
import { ABOUT_CONTENT } from './components/about/aboutContent';
import { EXPERIENCE_BADGE_CASES } from './components/about/experienceBadges';
import { BLOG_ENTRIES } from './components/blog/blogEntries';
import { CONTACT_OPTIONS } from './components/connect/contactOptions';
import { PROJECT_PARTY } from './components/projects/projectParty';

const NAV_ITEMS = [
  { href: '/about', route: 'about', label: 'About' },
  { href: '/projects', route: 'projects', label: 'Projects' },
  { href: '/writing', route: 'writing', label: 'Blog' },
  { href: '/contact', route: 'contact', label: 'Connect' },
];

const TRANSITION_TIMING = {
  close: 440,
  reveal: 560,
  finish: 1040,
};

function normalizePath(pathname) {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
}

function getRoute(pathname) {
  const path = normalizePath(pathname);
  if (path === '/') return { type: 'home' };
  if (path === '/about') return { type: 'about' };
  if (path === '/projects') return { type: 'projects' };
  if (path === '/writing' || path === '/blog') return { type: 'writing' };
  if (path === '/contact' || path === '/connect') return { type: 'contact' };

  const projectMatch = path.match(/^\/projects\/([^/]+)$/);
  if (projectMatch) {
    const project = PROJECT_PARTY.find((item) => item.id === projectMatch[1]);
    if (project) return { type: 'project', project };
  }

  return { type: 'not-found' };
}

function getRouteLabel(pathname) {
  const destination = getRoute(pathname);

  if (destination.type === 'project') return destination.project.title;

  return {
    home: 'Battle screen',
    about: 'Trainer profile',
    projects: 'Project party',
    writing: 'Field notes',
    contact: 'Communications',
    'not-found': 'Unknown area',
  }[destination.type];
}

function internalNavigate(event, href) {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  event.preventDefault();
  if (normalizePath(window.location.pathname) !== normalizePath(href)) {
    window.dispatchEvent(new CustomEvent('journal:request-navigation', {
      detail: { href },
    }));
  }
}

function JournalLink({ to, children, ...props }) {
  return (
    <a href={to} onClick={(event) => internalNavigate(event, to)} {...props}>
      {children}
    </a>
  );
}

function PokeballTransition({ phase, label }) {
  const isIdle = phase === 'idle';

  return (
    <>
      <div
        className={`route-transition is-${phase}`}
        aria-hidden="true"
      >
        <div className="route-transition-half route-transition-top" />
        <div className="route-transition-half route-transition-bottom" />
        <div className="route-transition-band" />
        <div className="route-transition-button"><span /></div>
        <p className="route-transition-copy">
          <span>Next destination</span>
          <strong>{label}</strong>
        </p>
      </div>
      <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
        {!isIdle ? `Opening ${label}` : ''}
      </div>
    </>
  );
}

function SiteHeader({ currentRoute }) {
  const activeType = currentRoute === 'project' ? 'projects' : currentRoute;

  return (
    <header className="game-header">
      <JournalLink className="game-brand" to="/" aria-label="Hassan Alawie, home">
        <span className="game-brand-mark">HA</span>
        <span>
          <strong>Hassan Alawie</strong>
          <small>Trainer No. 001</small>
        </span>
      </JournalLink>
      <nav className="game-nav" aria-label="Primary navigation">
        {NAV_ITEMS.map((item) => (
          <JournalLink
            key={item.href}
            to={item.href}
            className={activeType === item.route ? 'is-active' : ''}
            aria-current={activeType === item.route ? 'page' : undefined}
          >
            {item.label}
          </JournalLink>
        ))}
      </nav>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <p>Hassan Alawie · Data infrastructure, useful software, and neuroscience.</p>
      <div>
        <a href="https://github.com/hassanalawie" target="_blank" rel="noreferrer">GitHub ↗</a>
        <a href="https://www.linkedin.com/in/hassanalawie/" target="_blank" rel="noreferrer">LinkedIn ↗</a>
      </div>
    </footer>
  );
}

function HomePage() {
  return (
    <main id="main-content" className="battle-home">
      <img className="battle-backdrop" src={battleBackground} alt="" aria-hidden="true" />
      <div className="battle-sky-wash" aria-hidden="true" />

      <section className="battle-stage" aria-labelledby="battle-name">
        <div className="opponent-nameplate">
          <span>HASSAN</span>
          <span className="nameplate-level">ENGINEER · SF</span>
          <span className="nameplate-status" aria-hidden="true" />
        </div>

        <div className="battle-character battle-character-opponent" aria-hidden="true">
          <img className="battle-platform" src={battlePlatform} alt="" />
          <img className="battle-sprite" src={hassanTrainer} alt="" />
        </div>
        <div className="battle-character battle-character-player" aria-hidden="true">
          <img className="battle-platform" src={battlePlatform} alt="" />
          <img className="battle-sprite" src={playerTrainer} alt="" />
        </div>

        <div className="player-nameplate" aria-hidden="true">
          <span>VISITOR</span>
          <span className="nameplate-level">LV. 26</span>
          <span className="nameplate-status" />
        </div>

        <div className="battle-interface">
          <div className="battle-dialogue">
            <span className="battle-kicker">TRAINER ENCOUNTER</span>
            <h1 id="battle-name">You encountered Hassan!</h1>
            <p>Data infrastructure engineer exploring useful software, neuroscience, and AI.</p>
            <span className="battle-prompt">What will you do?</span>
          </div>
          <nav className="battle-command-menu" aria-label="Choose a destination">
            {NAV_ITEMS.map((item) => (
              <JournalLink key={item.href} to={item.href}>
                <span className="command-cursor" aria-hidden="true">▶</span>
                {item.label}
              </JournalLink>
            ))}
          </nav>
        </div>
      </section>
      <p className="battle-help">Use Tab to choose a command · Enter to open</p>
    </main>
  );
}

function ScreenTitle({ eyebrow, title, children }) {
  return (
    <header className="screen-title">
      <span>{eyebrow}</span>
      <h1>{title}</h1>
      {children ? <div>{children}</div> : null}
    </header>
  );
}

function AboutPage() {
  const allBadges = EXPERIENCE_BADGE_CASES.flatMap((group) => group.badges);
  const [selectedBadgeId, setSelectedBadgeId] = useState(allBadges[0].id);
  const selectedBadge = allBadges.find((badge) => badge.id === selectedBadgeId) || allBadges[0];

  return (
    <main id="main-content" className="game-page about-page">
      <ScreenTitle eyebrow="TRAINER PROFILE" title="Meet the trainer.">
        <p>Software, systems, and the brain—collected into one trainer record.</p>
      </ScreenTitle>

      <section className="trainer-card" aria-label="Trainer card for Hassan Alawie">
        <div className="trainer-card-id">
          <span className="trainer-card-label">TRAINER CARD</span>
          <div className="trainer-card-portrait">
            <img src={hassanTrainer} alt="Pixel portrait of Hassan as a Pokémon trainer" />
          </div>
          <div className="trainer-signature">
            <span>No. 001</span>
            <strong>{ABOUT_CONTENT.name}</strong>
            <small>{ABOUT_CONTENT.location}</small>
          </div>
        </div>
        <div className="trainer-card-copy">
          <p className="trainer-class">{ABOUT_CONTENT.title}</p>
          <p>{ABOUT_CONTENT.bio}</p>
          <dl className="trainer-stats">
            <div><dt>Current focus</dt><dd>{ABOUT_CONTENT.currentFocus}</dd></div>
            <div><dt>Party</dt><dd>{PROJECT_PARTY.length} project partners</dd></div>
            <div><dt>Interests</dt><dd>{ABOUT_CONTENT.tags.join(' · ')}</dd></div>
          </dl>
          <div className="trainer-card-actions">
            <a href={resumePdf} target="_blank" rel="noreferrer">Résumé ↗</a>
            {ABOUT_CONTENT.actions.map((action) => (
              <a key={action.label} href={action.href} target="_blank" rel="noreferrer">{action.label} ↗</a>
            ))}
          </div>
        </div>
      </section>

      <section className="badge-case-section" aria-labelledby="badge-case-title">
        <div className="section-label-row">
          <div>
            <span>EXPERIENCE</span>
            <h2 id="badge-case-title">Badge cases</h2>
          </div>
          <p>Choose a badge to inspect the role.</p>
        </div>

        <div className="badge-case-layout">
          <div className="badge-cabinet">
            {EXPERIENCE_BADGE_CASES.map((group) => (
              <section className="badge-case" key={group.id} aria-label={group.label}>
                <h3>{group.label}</h3>
                <div className="badge-case-tray">
                  {group.badges.map((badge) => (
                    <button
                      key={badge.id}
                      type="button"
                      className={selectedBadge.id === badge.id ? 'badge-button is-selected' : 'badge-button'}
                      onClick={() => setSelectedBadgeId(badge.id)}
                      aria-pressed={selectedBadge.id === badge.id}
                      aria-label={`${badge.name}, ${badge.role}, ${badge.detail}`}
                    >
                      <span className="badge-emblem"><img src={badge.logo} alt="" /></span>
                      <span>{badge.term}</span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <article className="badge-inspector" aria-live="polite">
            <span className="badge-inspector-label">SELECTED BADGE</span>
            <img src={selectedBadge.logo} alt="" aria-hidden="true" />
            <div>
              <p>{selectedBadge.term}</p>
              <h3>{selectedBadge.name}</h3>
              <strong>{selectedBadge.role}</strong>
              <span>{selectedBadge.detail}</span>
            </div>
            {selectedBadge.isCurrent ? <small>● CURRENT PARTY</small> : null}
          </article>
        </div>
      </section>

      <figure className="trainer-quote">
        <blockquote>“{ABOUT_CONTENT.quote}”</blockquote>
        <figcaption>— {ABOUT_CONTENT.quoteSource}</figcaption>
      </figure>
    </main>
  );
}

function PartyMember({ project, index }) {
  return (
    <li>
      <JournalLink
        className="party-slot"
        to={`/projects/${project.id}`}
        style={{ '--partner-accent': project.accent, '--partner-soft': project.accentSoft }}
      >
        <span className="party-cursor" aria-hidden="true">▶</span>
        <span className="party-sprite"><img src={project.image} alt="" aria-hidden="true" /></span>
        <span className="party-member-copy">
          <span className="party-member-topline">
            <strong>{project.title}</strong>
            <small>#{String(index + 1).padStart(2, '0')}</small>
          </span>
          <span className="party-purpose">{project.subtitle}</span>
          <span className="party-meter" aria-hidden="true"><i /></span>
          <span className="party-meta">
            <span>{project.pokemonName}</span>
            <span>{project.status}</span>
          </span>
        </span>
      </JournalLink>
    </li>
  );
}

function ProjectsPage() {
  return (
    <main id="main-content" className="game-page party-page">
      <section className="party-screen" aria-labelledby="party-title">
        <header className="party-screen-header">
          <div>
            <span>PROJECT PARTY</span>
            <h1 id="party-title">Choose a project.</h1>
          </div>
          <p>{PROJECT_PARTY.length} builds · {PROJECT_PARTY.length} partners</p>
        </header>
        <p className="party-instruction">Each party member carries a different experiment in products, research, or data.</p>
        <ol className="party-grid">
          {PROJECT_PARTY.map((project, index) => (
            <PartyMember key={project.id} project={project} index={index} />
          ))}
        </ol>
        <footer className="party-screen-footer">
          <span>▶ Select a member</span>
          <span>Status labels describe the project—not availability.</span>
        </footer>
      </section>
    </main>
  );
}

function ProjectGallery({ project }) {
  if (!project.gallery?.length) {
    return <p className="no-screens">No visual artifact is published for this project yet.</p>;
  }

  return (
    <div className={`summary-gallery summary-gallery-${project.gallery.length}`}>
      {project.gallery.map((image, index) => (
        <figure key={image.src} className={image.orientation === 'portrait' ? 'is-portrait' : ''}>
          <img src={image.src} alt={image.alt} />
          <figcaption>Fig. {String(index + 1).padStart(2, '0')} · {image.caption || image.alt}</figcaption>
        </figure>
      ))}
    </div>
  );
}

function ProjectPage({ project }) {
  const index = PROJECT_PARTY.findIndex((item) => item.id === project.id);
  const nextProject = PROJECT_PARTY[(index + 1) % PROJECT_PARTY.length];
  const links = (project.links || []).filter((link) => link.name && link.link);

  return (
    <main id="main-content" className="game-page project-summary-page">
      <section className="summary-screen" aria-labelledby="summary-title">
        <header className="summary-topbar">
          <JournalLink to="/projects">← Back to Party</JournalLink>
          <span>PROJECT SUMMARY</span>
          <span>#{String(index + 1).padStart(3, '0')}</span>
        </header>

        <div className="summary-layout">
          <aside className="summary-identity" style={{ '--partner-accent': project.accent }}>
            <span className="summary-type">{project.type}</span>
            <div className="summary-partner-sprite"><img src={project.image} alt="" aria-hidden="true" /></div>
            <small>PROJECT PARTNER</small>
            <strong>{project.pokemonName}</strong>
            <dl>
              <div><dt>Status</dt><dd>{project.status}</dd></div>
              <div><dt>Tools</dt><dd>{project.stack.join(' · ')}</dd></div>
            </dl>
          </aside>

          <article className="summary-content">
            <header className="summary-project-heading">
              <span>{project.eyebrow || project.subtitle}</span>
              <h1 id="summary-title">{project.title}</h1>
              <p>{project.summary}</p>
            </header>

            <nav className="summary-section-nav" aria-label="Project sections">
              <a href="#overview">Overview</a>
              <a href="#build">Build</a>
              <a href="#results">Results &amp; Notes</a>
              <a href="#screens">Screens &amp; Links</a>
            </nav>

            <section id="overview" className="summary-section">
              <span>01 · OVERVIEW</span>
              <h2>What this project is</h2>
              <p>{project.description}</p>
            </section>

            <section id="build" className="summary-section summary-section-split">
              <div>
                <span>02 · BUILD</span>
                <h2>My contribution</h2>
                <p>{project.role}</p>
              </div>
              <div className="summary-callout">
                <strong>Hard part</strong>
                <p>{project.details.challenge}</p>
              </div>
            </section>

            <section id="results" className="summary-section">
              <span>03 · RESULTS &amp; NOTES</span>
              <h2>What came out of it</h2>
              <p className="summary-result">{project.impact}</p>
              <dl className="summary-notes">
                <div><dt>Favorite detail</dt><dd>{project.details.feature}</dd></div>
                <div><dt>What I learned</dt><dd>{project.details.learned}</dd></div>
              </dl>
            </section>

            <section id="screens" className="summary-section">
              <span>04 · SCREENS &amp; LINKS</span>
              <h2>Artifacts from the build</h2>
              <ProjectGallery project={project} />
              {links.length ? (
                <div className="summary-links">
                  {links.map((link) => (
                    <a key={link.name} href={link.link} target="_blank" rel="noreferrer">{link.name} ↗</a>
                  ))}
                </div>
              ) : <p className="no-screens">No public demo or repository is listed for this project.</p>}
            </section>

            <nav className="summary-next" aria-label="Next project">
              <span>Next party member</span>
              <JournalLink to={`/projects/${nextProject.id}`}>{nextProject.title} →</JournalLink>
            </nav>
          </article>
        </div>
      </section>
    </main>
  );
}

function WritingPage() {
  return (
    <main id="main-content" className="game-page archive-page">
      <ScreenTitle eyebrow="TRAINER NOTEBOOK" title="Field notes.">
        <p>Writing about browsers, debugging, scraping, neuroscience, and the small discoveries that make technical work easier to understand.</p>
      </ScreenTitle>

      <section className="notebook-screen" aria-labelledby="notebook-title">
        <header><h2 id="notebook-title">Archive index</h2><span>{String(BLOG_ENTRIES.length).padStart(2, '0')} entries</span></header>
        <ol>
          {BLOG_ENTRIES.map((entry) => {
            const isSearchDestination = entry.mediumUrl.includes('/search?');
            return (
              <li key={entry.id} className="notebook-entry">
                <span className="notebook-number">{entry.entryNumber}</span>
                <img src={entry.image} alt="" aria-hidden="true" />
                <article>
                  <time dateTime={entry.date}>{entry.displayDate}</time>
                  <h3>{entry.title}</h3>
                  <p>{entry.description}</p>
                  <a href={entry.mediumUrl} target="_blank" rel="noreferrer">
                    {isSearchDestination ? 'Find on Medium' : 'Read on Medium'} ↗
                  </a>
                </article>
              </li>
            );
          })}
        </ol>
      </section>
    </main>
  );
}

function ContactPage() {
  return (
    <main id="main-content" className="game-page connect-page">
      <section className="communication-screen" aria-labelledby="connect-title">
        <header>
          <span>COMMUNICATIONS</span>
          <h1 id="connect-title">Let’s trade notes.</h1>
          <p>Data infrastructure, useful software, neuroscience, AI—or something between them.</p>
        </header>
        <div className="communication-options">
          {CONTACT_OPTIONS.map((option, index) => (
            <a key={option.id} href={option.href} target="_blank" rel="noreferrer">
              <span className="communication-cursor" aria-hidden="true">▶</span>
              <span className="communication-number">0{index + 1}</span>
              <span className="communication-icon" aria-hidden="true">{option.icon}</span>
              <span><strong>{option.label}</strong><small>{option.description}</small></span>
              <span aria-hidden="true">↗</span>
            </a>
          ))}
        </div>
        <footer>
          <img src={hassanTrainer} alt="" aria-hidden="true" />
          <p>Choose a channel. These are the two places I actually use.</p>
        </footer>
      </section>
    </main>
  );
}

function NotFoundPage() {
  return (
    <main id="main-content" className="game-page not-found-page">
      <span>ERROR 404</span>
      <h1>This area isn’t on the map.</h1>
      <p>The route may have moved, or it has not been discovered yet.</p>
      <JournalLink to="/">Return to battle →</JournalLink>
    </main>
  );
}

function App() {
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const [transition, setTransition] = useState({ phase: 'idle', label: '' });
  const pathnameRef = useRef(pathname);
  const transitionInFlight = useRef(false);
  const transitionTimers = useRef([]);
  const route = useMemo(() => getRoute(pathname), [pathname]);

  useEffect(() => {
    const clearTransitionTimers = () => {
      transitionTimers.current.forEach((timer) => window.clearTimeout(timer));
      transitionTimers.current = [];
    };

    const focusPage = () => {
      window.requestAnimationFrame(() => {
        const main = document.getElementById('main-content');
        if (!main) return;
        main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
      });
    };

    const commitRoute = (href, shouldPush) => {
      if (shouldPush) window.history.pushState({}, '', href);
      pathnameRef.current = window.location.pathname;
      setPathname(window.location.pathname);
      window.scrollTo({ top: 0, behavior: 'auto' });
    };

    const runTransition = (href, shouldPush = true) => {
      const nextPath = normalizePath(href);
      if (normalizePath(pathnameRef.current) === nextPath) return;

      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) {
        commitRoute(href, shouldPush);
        focusPage();
        return;
      }

      if (transitionInFlight.current) return;
      transitionInFlight.current = true;
      clearTransitionTimers();

      const label = getRouteLabel(href);
      setTransition({ phase: 'closing', label });

      transitionTimers.current.push(window.setTimeout(() => {
        commitRoute(href, shouldPush);
        setTransition({ phase: 'covered', label });
      }, TRANSITION_TIMING.close));

      transitionTimers.current.push(window.setTimeout(() => {
        setTransition({ phase: 'opening', label });
      }, TRANSITION_TIMING.reveal));

      transitionTimers.current.push(window.setTimeout(() => {
        setTransition({ phase: 'idle', label: '' });
        transitionInFlight.current = false;
        focusPage();
      }, TRANSITION_TIMING.finish));
    };

    const requestNavigation = (event) => {
      const href = event.detail?.href;
      if (typeof href === 'string') runTransition(href);
    };

    const syncHistoryNavigation = () => {
      if (transitionInFlight.current) {
        clearTransitionTimers();
        transitionInFlight.current = false;
        pathnameRef.current = window.location.pathname;
        setPathname(window.location.pathname);
        setTransition({ phase: 'idle', label: '' });
        focusPage();
        return;
      }

      runTransition(window.location.pathname, false);
    };

    window.addEventListener('popstate', syncHistoryNavigation);
    window.addEventListener('journal:request-navigation', requestNavigation);
    return () => {
      clearTransitionTimers();
      window.removeEventListener('popstate', syncHistoryNavigation);
      window.removeEventListener('journal:request-navigation', requestNavigation);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    document.title = route.type === 'project'
      ? `${route.project.title} — Hassan Alawie`
      : {
          home: 'Hassan Alawie — Data Infrastructure Engineer',
          about: 'About — Hassan Alawie',
          projects: 'Projects — Hassan Alawie',
          writing: 'Blog — Hassan Alawie',
          contact: 'Connect — Hassan Alawie',
          'not-found': 'Not Found — Hassan Alawie',
        }[route.type];
  }, [route]);

  let content;
  if (route.type === 'home') content = <HomePage />;
  if (route.type === 'about') content = <AboutPage />;
  if (route.type === 'projects') content = <ProjectsPage />;
  if (route.type === 'project') content = <ProjectPage project={route.project} />;
  if (route.type === 'writing') content = <WritingPage />;
  if (route.type === 'contact') content = <ContactPage />;
  if (route.type === 'not-found') content = <NotFoundPage />;

  const isHome = route.type === 'home';

  return (
    <div className={isHome ? 'site-surround is-battle-home' : 'site-surround'}>
      <PokeballTransition phase={transition.phase} label={transition.label} />
      <a className="skip-link" href="#main-content">Skip to content</a>
      {isHome ? content : (
        <div className="game-site-shell">
          <SiteHeader currentRoute={route.type} />
          {content}
          <SiteFooter />
        </div>
      )}
    </div>
  );
}

export default App;
